import { inject, Injectable } from '@angular/core';
import { AdminService } from '@/services/admin.service';
import { UserService } from '@/services/user.service';
import { LogisticsService } from 'src/app/modules/logistics/services/logistics.service';
import {
  DashboardDefinition,
  DashboardProfile,
  DashboardProfilesContext,
  DashboardWidgetDefinition,
  ProfileFilterCondition,
  ProfileFilters,
} from '../models/dashboard.models';
import { map, Observable, of, shareReplay, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DashboardDataService {
  private readonly userService = inject(UserService);
  private readonly adminService = inject(AdminService);
  private readonly logisticsService = inject(LogisticsService);
  private readonly baseShipmentsCache = new Map<string, Observable<unknown[]>>();

  clearCache(): void {
    this.baseShipmentsCache.clear();
  }

  getDashboardDefinition(): Observable<DashboardDefinition> {
    return this.userService.getDashboard().pipe(map((raw) => this.parseDashboardDefinition(raw)));
  }

  getProfilesContext(forceRefresh = false): Observable<DashboardProfilesContext> {
    return this.adminService.getAllUserProfiles(forceRefresh).pipe(
      map((res) => ({
        profiles: Array.isArray(res?.profiles) ? (res.profiles as DashboardProfile[]) : [],
        filterGroups: Array.isArray(res?.filters) ? (res.filters as unknown[][]) : [],
      })),
    );
  }

  getWidgetData(
    widget: DashboardWidgetDefinition,
    context: DashboardProfilesContext,
    forceRefresh = false,
  ): Observable<unknown[]> {
    if (widget.moduleType !== 'logistics') {
      return of([]);
    }

    return this.getOrLoadBaseShipments(context, forceRefresh).pipe(
      map((rows) => this.filterByProfile(rows, widget.profileName, context.profiles)),
    );
  }

  getBaseShipments(context: DashboardProfilesContext, forceRefresh = false): Observable<unknown[]> {
    return this.getOrLoadBaseShipments(context, forceRefresh);
  }

  projectWidgetData(
    widget: DashboardWidgetDefinition,
    baseRows: unknown[],
    profiles: DashboardProfile[],
  ): unknown[] {
    if (widget.moduleType !== 'logistics') {
      return [];
    }

    return this.filterByProfile(baseRows, widget.profileName, profiles);
  }

  private getOrLoadBaseShipments(
    context: DashboardProfilesContext,
    forceRefresh: boolean,
  ): Observable<unknown[]> {
    const cacheKey = this.buildContextCacheKey(context);
    if (forceRefresh || !this.baseShipmentsCache.has(cacheKey)) {
      const request$ = this.logisticsService
        .getAllShipments(context.filterGroups)
        .pipe(shareReplay(1));
      this.baseShipmentsCache.set(cacheKey, request$);
    }

    return this.baseShipmentsCache.get(cacheKey)!;
  }

  private buildContextCacheKey(context: DashboardProfilesContext): string {
    return JSON.stringify(context.filterGroups ?? []);
  }

  private parseDashboardDefinition(raw: unknown): DashboardDefinition {
    const fallback: DashboardDefinition = {
      layout: { columns: 15, rowHeight: 120, gap: 16 },
      widgets: [],
    };

    if (!raw) return fallback;

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as DashboardDefinition;
        return this.isDashboardDefinition(parsed) ? this.normalizeDefinition(parsed) : fallback;
      } catch {
        return fallback;
      }
    }

    if (this.isDashboardDefinition(raw)) {
      return this.normalizeDefinition(raw);
    }

    const maybeWrapped = (raw as { data?: { item?: unknown } })?.data?.item;
    if (typeof maybeWrapped === 'string') {
      try {
        const parsed = JSON.parse(maybeWrapped) as DashboardDefinition;
        return this.isDashboardDefinition(parsed) ? this.normalizeDefinition(parsed) : fallback;
      } catch {
        return fallback;
      }
    }

    return fallback;
  }

  private isDashboardDefinition(value: unknown): value is DashboardDefinition {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as DashboardDefinition;
    return !!candidate.layout && Array.isArray(candidate.widgets);
  }

  private filterByProfile(
    data: unknown[],
    profileName: string | undefined,
    profiles: DashboardProfile[],
  ): unknown[] {
    if (!profileName) return data;
    const profile = profiles.find((p) => p.name === profileName);
    if (!profile?.filters) return data;
    return this.applyFilters(data, profile.filters);
  }

  private applyFilters(data: unknown[], filters: ProfileFilters): unknown[] {
    return data.filter((item) => {
      return Object.entries(filters).every(([field, conditions]) => {
        const activeConditions = conditions.filter(
          (cond) => !this.isEmptyFilterValue(cond) || this.isNoValueMatchMode(cond.matchMode),
        );
        if (!activeConditions.length) {
          return true;
        }

        const row = item as Record<string, unknown>;
        const itemValue = row[field];
        const operator = this.resolveConditionsOperator(activeConditions);
        const evaluation = activeConditions.map((cond) =>
          this.matchCondition(itemValue, cond.value, cond.matchMode ?? 'contains'),
        );

        return operator === 'or' ? evaluation.some(Boolean) : evaluation.every(Boolean);
      });
    });
  }

  private resolveConditionsOperator(
    conditions: (ProfileFilterCondition & { operator?: string })[],
  ): 'and' | 'or' {
    const normalized = String(conditions[0]?.operator ?? 'and').toLowerCase();
    return normalized === 'or' ? 'or' : 'and';
  }

  private isEmptyFilterValue(cond: ProfileFilterCondition): boolean {
    return cond.value === null || cond.value === undefined || cond.value === '';
  }

  private isNoValueMatchMode(matchMode: unknown): boolean {
    return matchMode === 'isEmpty' || matchMode === 'isNotEmpty';
  }

  private matchCondition(itemValue: unknown, filterValue: unknown, mode: string): boolean {
    switch (mode) {
      case 'notEquals':
      case 'isNot':
        return String(itemValue ?? '').toLowerCase() !== String(filterValue ?? '').toLowerCase();

      case 'equals':
      case 'is':
        return String(itemValue ?? '').toLowerCase() === String(filterValue ?? '').toLowerCase();
    }

    if (itemValue == null) {
      return false;
    }

    const itemText = String(itemValue).toLowerCase();
    const filterText = String(filterValue).toLowerCase();

    switch (mode) {
      case 'startsWith':
        return itemText.startsWith(filterText);

      case 'contains':
        return itemText.includes(filterText);

      case 'endsWith':
        return itemText.endsWith(filterText);

      case 'in':
        return Array.isArray(filterValue) ? filterValue.includes(itemValue) : false;

      case 'lt':
        return Number(itemValue) < Number(filterValue);

      case 'lte':
        return Number(itemValue) <= Number(filterValue);

      case 'gt':
        return Number(itemValue) > Number(filterValue);

      case 'gte':
        return Number(itemValue) >= Number(filterValue);

      case 'before':
        return new Date(String(itemValue)) < new Date(String(filterValue));

      case 'after':
        return new Date(String(itemValue)) > new Date(String(filterValue));

      case 'dateIs':
        return (
          new Date(String(itemValue)).toDateString() ===
          new Date(String(filterValue)).toDateString()
        );

      default:
        return true;
    }
  }

  private normalizeDefinition(definition: DashboardDefinition): DashboardDefinition {
    return {
      ...definition,
      widgets: (definition.widgets ?? []).map((widget) => this.normalizeWidget(widget)),
    };
  }

  private normalizeWidget(widget: DashboardWidgetDefinition): DashboardWidgetDefinition {
    if (!widget.config || typeof widget.config !== 'object') {
      return widget;
    }

    const config = widget.config as Record<string, unknown>;
    const normalizedConfig = this.normalizeTransportFieldAliases(widget.type, config);

    if (widget.type !== 'calc') {
      return {
        ...widget,
        config: normalizedConfig,
      };
    }

    const showOnlyValue = normalizedConfig['showOnlyValue'];
    if (typeof showOnlyValue !== 'string' || !showOnlyValue.trim()) {
      return {
        ...widget,
        config: normalizedConfig,
      };
    }

    return {
      ...widget,
      config: {
        ...normalizedConfig,
        showOnlyValue: this.normalizeTransportValue(showOnlyValue),
      },
    };
  }

  private normalizeTransportFieldAliases(
    widgetType: DashboardWidgetDefinition['type'],
    config: Record<string, unknown>,
  ): Record<string, unknown> {
    const result = { ...config };

    if (widgetType === 'calc') {
      if (result['groupField'] === 'transportModeWithIcon') {
        result['groupField'] = 'modeOfTransport';
      }

      if (result['calcField'] === 'transportModeWithIcon') {
        result['calcField'] = 'modeOfTransport';
      }
    }

    if (widgetType === 'piechart' && result['fieldName'] === 'transportModeWithIcon') {
      result['fieldName'] = 'modeOfTransport';
    }

    return result;
  }

  private normalizeTransportValue(value: string): string {
    const cleaned = value.replace(/[\u{1F300}-\u{1FAFF}\u2600-\u27BF]/gu, '').trim();
    const normalized = cleaned.replace(/\s+/g, ' ').toLowerCase();

    switch (normalized) {
      case 'air':
        return 'Air';
      case 'sea':
        return 'Sea';
      case 'road':
        return 'Road';
      default:
        return cleaned;
    }
  }
}
